import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { subscribeToUserCollection } from '../utils/userDataHelper';
import Breadcrumbs from '../Components/Breadcrumbs';
import Footer from '../Components/Footer';
import './labcss/labels-print.css';
import './labcss/print-sheets.css';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { normalizeRecipe, weightToGrams } from '../utils/recipeSchema';
import { getPricePerGram, getCostPerUnit } from './utils/costCalculations';

/**
 * PrintCenter - Central hub for all printable documents
 * Route: /lab/print
 */

const num = (v, def = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

const PrintOverlay = ({ children }) => {
  useEffect(() => {
    document.body.dataset.soaplabPrint = 'true';
    return () => { delete document.body.dataset.soaplabPrint; };
  }, []);
  return createPortal(<div className="print-overlay" role="dialog" aria-modal="true" aria-label="Printable document">{children}</div>, document.body);
};

// ==========================================
// 1. RECIPE PRINT SHEET
// ==========================================
export const RecipePrintSheet = ({ recipe: rawRecipe, onClose, formatMoney }) => {
  const recipe = normalizeRecipe(rawRecipe);
  const money = value => formatMoney(value);
  const handlePrint = () => {
    window.print();
  };

  const oils = recipe.oils || recipe.items || [];
  const batchMass = num(recipe.computedBatchMass || recipe.batchSize, 0);
  const bars = num(recipe.bars, 1);
  const naoh = num(recipe.lyeRequired, 0);
  const water = num(recipe.waterWeight, 0);
  const superfat = num(recipe.superfat, 6);
  const lyeConc = num(recipe.actualLyeConcentration ?? recipe.lyeConcentration, 0.3);

  return (
    <PrintOverlay>
      <div className="print-sheet">
        {/* Header */}
        <div className="print-header">
          <h1 className="print-title">🧪 {recipe.name || 'Untitled Recipe'}</h1>
          <p className="print-subtitle">
            Use: {(recipe.use || ['body']).join(', ')} | Skin Type: {(recipe.skinType || ['all']).join(', ')} | Goal: {recipe.goal || 'balanced'}
          </p>
          <p className="print-subtitle">Printed: {new Date().toLocaleDateString()}</p>
        </div>

        <div className="print-grid">
          {/* Batch Summary */}
          <div className="print-section">
            <div className="print-section-title">📊 Batch Summary</div>
            <div className="print-summary-box">
              <div className="print-summary-row"><span>Total Batch Mass:</span><strong>{batchMass.toFixed(0)} g</strong></div>
              <div className="print-summary-row"><span>Number of Bars:</span><strong>{bars}</strong></div>
              <div className="print-summary-row"><span>Bar Weight:</span><strong>{num(recipe.barWeight, 100)} g</strong></div>
              <div className="print-summary-row"><span>Superfat:</span><strong>{superfat}%</strong></div>
              <div className="print-summary-row"><span>Lye Concentration:</span><strong>{(lyeConc * 100).toFixed(0)}%</strong></div>
            </div>
          </div>

          {/* Lye & Water */}
          <div className="print-section">
            <div className="print-section-title">⚗️ Lye Solution</div>
            <div className="print-summary-box">
              <div className="print-summary-row"><span>{recipe.dualLye ? 'Total Alkali' : recipe.lyeType} Required:</span><strong>{naoh.toFixed(2)} g</strong></div>
              <div className="print-summary-row"><span>Water Required:</span><strong>{water.toFixed(2)} g</strong></div>
              {recipe.dualLye && <><div className="print-summary-row"><span>NaOH:</span><strong>{num(recipe.alkaliWeights.NaOH).toFixed(2)} g</strong></div>
                <div className="print-summary-row"><span>KOH:</span><strong>{num(recipe.alkaliWeights.KOH).toFixed(2)} g</strong></div></>}
              <div className="print-summary-row"><span>Total Lye Solution:</span><strong>{(naoh + water).toFixed(2)} g</strong></div>
              <div className="print-summary-row"><span>Alkali:</span><strong>{recipe.dualLye ? 'NaOH + KOH' : recipe.lyeType}</strong></div>
            </div>
          </div>
        </div>

        {/* Oils Table */}
        <div className="print-section">
          <div className="print-section-title">🫒 Oils ({oils.length})</div>
          <table className="print-table">
            <thead>
              <tr>
                <th>Oil</th>
                <th>Percentage</th>
                <th>Weight (g)</th>
                <th>SAP</th>
                <th>NaOH at 0% superfat (g)</th>
              </tr>
            </thead>
            <tbody>
              {(oils).map((oil, i) => (
                <tr key={i}>
                  <td>{oil.name}</td>
                  <td>{num(oil.pct, 0).toFixed(1)}%</td>
                  <td>{num(oil.weight, (oil.pct / 100) * num(recipe.totalOilWeight, 1000)).toFixed(1)}</td>
                  <td>{num(oil.sap, 0).toFixed(3)}</td>
                  <td>{num(oil.naohForOil, num(oil.sap, 0) * num(oil.weight, 0)).toFixed(2)}</td>
                </tr>
              ))}
              <tr style={{ fontWeight: 'bold', background: '#f0f0f0' }}>
                <td>TOTAL</td>
                <td>{oils.reduce((s, o) => s + num(o.pct, 0), 0).toFixed(1)}%</td>
                <td>{num(recipe.totalOilWeight, 1000).toFixed(0)}</td>
                <td>—</td>
                <td>{oils.reduce((sum, oil) => sum + num(oil.naohForOil, num(oil.sap) * num(oil.weight)), 0).toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Additives */}
        {recipe.additives?.length > 0 && (
          <div className="print-section">
            <div className="print-section-title">✨ Additives ({recipe.additives.length})</div>
            <table className="print-table">
              <thead>
                <tr>
                  <th>Additive</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>When Added</th>
                </tr>
              </thead>
              <tbody>
                {recipe.additives.map((add, i) => (
                  <tr key={i}>
                    <td>{add.name}</td>
                    <td>{add.type || '—'}</td>
                    <td>{add.amount}{add.unit}</td>
                    <td>{add.whenAdded || add.appliesTo || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Cost & Pricing */}
        <div className="print-section">
          <div className="print-section-title">💰 Cost & Pricing</div>
          <div className="print-grid">
            <div className="print-summary-box">
              <div className="print-summary-row"><span>Material Cost:</span><strong>{money(num(recipe.cost?.material, 0))}</strong></div>
              <div className="print-summary-row"><span>Working Cost:</span><strong>{money(num(recipe.cost?.working, 0))}</strong></div>
              <div className="print-summary-row"><span>Packaging:</span><strong>{money(num(recipe.cost?.packaging, 0))}</strong></div>
              <div className="print-summary-row" style={{ borderTop: '1px solid #ccc', paddingTop: '5px', marginTop: '5px' }}>
                <span>Total Cost:</span><strong>{money(num(recipe.totals?.totalCost, 0))}</strong>
              </div>
            </div>
            <div className="print-summary-box">
              <div className="print-summary-row"><span>Cost per Bar:</span><strong>{money(num(recipe.totals?.perBar, 0))}</strong></div>
              <div className="print-summary-row"><span>Markup:</span><strong>{recipe.markupPercent}%</strong></div>
              <div className="print-summary-row" style={{ borderTop: '1px solid #ccc', paddingTop: '5px', marginTop: '5px' }}>
                <span>Selling Price:</span><strong>{money(num(recipe.pricingOptions?.computedSellingPerBar || recipe.pricing?.sellingPrice, 0))}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Notes */}
        {recipe.notes && (
          <div className="print-section">
            <div className="print-section-title">📝 Notes</div>
            <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{recipe.notes}</p>
          </div>
        )}

        {/* Footer */}
        <div className="print-footer">
          <p>Recipe ID: {recipe.id || 'Not saved'} | Created: {recipe.createdAt ? new Date(recipe.createdAt?.toDate?.() || recipe.createdAt).toLocaleDateString() : 'N/A'}</p>
        </div>

        {/* Print buttons - hidden when printing */}
        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Recipe
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 2. PRODUCTION BATCH SHEET
// ==========================================
export const ProductionBatchSheet = ({ recipe: rawRecipe, onClose }) => {
  const recipe = normalizeRecipe(rawRecipe);
  const handlePrint = () => window.print();
  
  const oils = recipe.oils || recipe.items || [];
  const naoh = num(recipe.lyeRequired, 0);
  const water = num(recipe.waterWeight, 0);

  return (
    <PrintOverlay>
      <style>{`
        .checkbox-item { display: flex; align-items: flex-start; gap: 10px; margin: 8px 0; }
        .checkbox-box { width: 18px; height: 18px; border: 2px solid #333; flex-shrink: 0; margin-top: 2px; }
        .step-section { margin: 15px 0; padding: 12px; background: #f9f9f9; border-radius: 6px; }
        .step-title { font-weight: bold; margin-bottom: 10px; font-size: 11pt; }
        .warning-box { background: #fff3e0; border-left: 4px solid #ff9800; padding: 10px; margin: 10px 0; }
        .danger-box { background: #ffebee; border-left: 4px solid #f44336; padding: 10px; margin: 10px 0; }
      `}</style>
      
      <div className="print-sheet">
        <div className="print-header">
          <h1 className="print-title">📋 Production Batch Sheet</h1>
          <p className="print-subtitle">{recipe.name || 'Untitled Recipe'} — {new Date().toLocaleDateString()}</p>
        </div>

        {/* Safety Warning */}
        <div className="danger-box">
          <strong>⚠️ SAFETY FIRST:</strong> Follow the current supplier safety data sheet (SDS) for each alkali, concentration and container material. Confirm suitable PPE, ventilation, eyewash and an approved spill plan before starting.
        </div>

        {/* Step 1: Preparation */}
        <div className="step-section">
          <div className="step-title">Step 1: Preparation</div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Gather all equipment (scale, thermometer, stick blender, molds)</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Put on safety gear (goggles, gloves, long sleeves)</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Prepare workspace with newspaper/plastic sheet</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Keep the supplier safety data sheet and appropriate spill supplies available</span></div>
        </div>

        {/* Step 2: Measure Oils */}
        <div className="step-section">
          <div className="step-title">Step 2: Measure Oils (Total: {num(recipe.totalOilWeight, 1000).toFixed(0)}g)</div>
          {(oils).map((oil, i) => (
            <div key={i} className="checkbox-item">
              <div className="checkbox-box"></div>
              <span><strong>{oil.name}</strong>: {num(oil.weight, (oil.pct / 100) * num(recipe.totalOilWeight, 1000)).toFixed(1)}g ({num(oil.pct, 0).toFixed(1)}%)</span>
            </div>
          ))}
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Melt solid oils if needed, then combine all oils</span></div>
        </div>

        {/* Step 3: Prepare Lye Solution */}
        <div className="step-section">
          <div className="step-title">Step 3: Prepare Lye Solution</div>
          <div className="warning-box">
            <strong>⚡ Always add lye TO water, never water to lye!</strong>
          </div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Measure <strong>{water.toFixed(1)}g water</strong> into heat-safe container</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Measure <strong>{naoh.toFixed(1)}g {recipe.dualLye ? 'total alkali' : recipe.lyeType}</strong>{recipe.dualLye && ` (${num(recipe.alkaliWeights.NaOH).toFixed(1)}g NaOH + ${num(recipe.alkaliWeights.KOH).toFixed(1)}g KOH)`}</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Slowly add lye to water while stirring</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Allow to cool (target: 100-110°F / 38-43°C)</span></div>
        </div>

        {/* Step 4: Additives Pre-Lye */}
        {recipe.additives?.filter(a => a.appliesTo === 'lye' || a.whenAdded === 'pre-lye').length > 0 && (
          <div className="step-section">
            <div className="step-title">Step 4a: Pre-Lye Additives</div>
            {recipe.additives.filter(a => a.appliesTo === 'lye' || a.whenAdded === 'pre-lye').map((add, i) => (
              <div key={i} className="checkbox-item">
                <div className="checkbox-box"></div>
                <span>Add <strong>{add.name}</strong>: {add.amount}{add.unit} to lye solution</span>
              </div>
            ))}
          </div>
        )}

        {/* Step 5: Combine */}
        <div className="step-section">
          <div className="step-title">Step 4: Combine Oils & Lye</div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Check temperatures: Oils ___°F  Lye ___°F (both should be 100-110°F)</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Slowly pour lye solution into oils</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Blend with stick blender until light trace</span></div>
        </div>

        {/* Step 6: Post-trace Additives */}
        {recipe.additives?.filter(a => a.appliesTo === 'post' || a.whenAdded === 'post-trace').length > 0 && (
          <div className="step-section">
            <div className="step-title">Step 5: Post-Trace Additives</div>
            {recipe.additives.filter(a => a.appliesTo === 'post' || a.whenAdded === 'post-trace').map((add, i) => (
              <div key={i} className="checkbox-item">
                <div className="checkbox-box"></div>
                <span>Add <strong>{add.name}</strong>: {add.amount}{add.unit}</span>
              </div>
            ))}
            <div className="checkbox-item"><div className="checkbox-box"></div><span>Blend briefly to incorporate</span></div>
          </div>
        )}

        {/* Step 7: Mold */}
        <div className="step-section">
          <div className="step-title">Step 6: Pour & Mold</div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Pour into prepared mold(s)</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Tap mold to release air bubbles</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Cover with plastic wrap or cardboard</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Insulate with towels if desired</span></div>
        </div>

        {/* Step 8: Unmold & Cure */}
        <div className="step-section">
          <div className="step-title">Step 7: Unmold & Cure</div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Record the approved unmolding time for this formulation and actual batch conditions</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Cut into {num(recipe.bars, 10)} bars (~{num(recipe.barWeight, 100)}g each)</span></div>
          <div className="checkbox-item"><div className="checkbox-box"></div><span>Follow the documented cure/storage procedure and release the batch only after its required QC review</span></div>
        </div>

        {/* Notes */}
        <div className="step-section">
          <div className="step-title">📝 Batch Notes</div>
          <div style={{ border: '1px solid #ccc', minHeight: 80, padding: 10 }}>
            <p style={{ margin: 0, color: '#666' }}>Date made: ____________  Time: ____________</p>
            <p style={{ margin: '10px 0 0 0', color: '#666' }}>Observations: </p>
          </div>
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Production Sheet
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 3. CURING TRACKER SHEET
// ==========================================
export const CuringTrackerSheet = ({ batches = [], onClose }) => {
  const handlePrint = () => window.print();

  return (
    <PrintOverlay>
      <style>{`
        .cure-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        .cure-table th, .cure-table td { border: 1px solid #ccc; padding: 6px; text-align: center; }
        .cure-table th { background: #f5f5f5; }
        .week-cell { min-width: 50px; }
      `}</style>
      
      <div className="print-sheet">
        <div className="print-header">
          <h1 className="print-title">📅 Curing Tracker</h1>
          <p className="print-subtitle">Record measurements under your formulation-specific cure and QC procedure</p>
        </div>

        <table className="cure-table">
          <thead>
            <tr>
              <th>Batch Name</th>
              <th>Date Made</th>
              <th>Ready Date</th>
              <th className="week-cell">Week 1</th>
              <th className="week-cell">Week 2</th>
              <th className="week-cell">Week 3</th>
              <th className="week-cell">Week 4</th>
              <th className="week-cell">Week 5</th>
              <th className="week-cell">Week 6</th>
              <th>pH Test</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {batches.length > 0 ? batches.map((batch, i) => {
              const madeDate = batch.madeDate ? new Date(batch.madeDate) : new Date();
              const readyDate = new Date(madeDate);
              readyDate.setDate(readyDate.getDate() + 42); // 6 weeks
              return (
                <tr key={i}>
                  <td>{batch.name || batch.recipeName || `Batch ${i + 1}`}</td>
                  <td>{madeDate.toLocaleDateString()}</td>
                  <td>{readyDate.toLocaleDateString()}</td>
                  <td></td><td></td><td></td><td></td><td></td><td></td>
                  <td></td>
                  <td></td>
                </tr>
              );
            }) : (
              // Empty rows for manual entry
              Array(10).fill(0).map((_, i) => (
                <tr key={i}>
                  <td style={{ minWidth: 120 }}></td>
                  <td></td><td></td>
                  <td></td><td></td><td></td><td></td><td></td><td></td>
                  <td></td>
                  <td style={{ minWidth: 100 }}></td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div style={{ marginTop: 20, fontSize: '9pt' }}>
          <strong>Weight Tracking:</strong> Record sample identity, weight, date and storage conditions. Compare changes with your documented specification.
          <br/><strong>pH Testing:</strong> Record the validated method, sample dilution, temperature and instrument calibration. A pH reading or elapsed cure time alone does not establish batch acceptability.
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Curing Tracker
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 4. SOAP LABELS GENERATOR
// ==========================================
const SoapLabelsSheet = ({ recipe: rawRecipe, onClose }) => {
  const recipe = normalizeRecipe(rawRecipe);
  const [labelsPerPage, setLabelsPerPage] = useState(8);
  const handlePrint = () => window.print();

  const oils = recipe.oils || recipe.items || [];
  const oilNames = oils.map(o => o.name).join(', ');

  const labelStyle = {
    width: labelsPerPage === 8 ? '90mm' : '63mm',
    height: labelsPerPage === 8 ? '60mm' : '38mm',
    border: '1px dashed #ccc',
    padding: '8px',
    margin: '2mm',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    fontSize: labelsPerPage === 8 ? '8pt' : '6pt',
    pageBreakInside: 'avoid',
  };

  return (
    <PrintOverlay>
      <div className="print-sheet" style={{ padding: '10mm' }}>
        <div className="no-print" style={{ marginBottom: 20, display: 'flex', gap: 10, alignItems: 'center' }}>
          <label>Labels per page:</label>
          <select value={labelsPerPage} onChange={e => setLabelsPerPage(Number(e.target.value))} style={{ padding: 8, borderRadius: 4 }}>
            <option value={8}>8 (Large - 90x60mm)</option>
            <option value={21}>21 (Small - 63x38mm)</option>
          </select>
        </div>

        <div className="label-grid" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
          {Array(labelsPerPage).fill(0).map((_, i) => (
            <div key={i} style={labelStyle}>
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '1.2em', marginBottom: 4 }}>{recipe.name || 'Handmade Soap'}</div>
                <div style={{ color: '#666', marginBottom: 4 }}>{(recipe.use || ['Body']).join(' & ')} Soap</div>
              </div>
              <div style={{ fontSize: '0.9em', color: '#444' }}>
                <strong>Ingredients:</strong> {oilNames}, Water, Sodium Hydroxide
                {recipe.additives?.length > 0 && `, ${recipe.additives.map(a => a.name).join(', ')}`}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto', paddingTop: 4, borderTop: '1px solid #eee', fontSize: '0.85em' }}>
                <span>Net Wt: {num(recipe.barWeight, 100)}g</span>
                <span>Batch: ______</span>
              </div>
            </div>
          ))}
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Labels
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 5. SHOPPING / REORDER LIST
// ==========================================
const ShoppingListSheet = ({ ingredients = [], recipes = [], onClose, formatMoney }) => {
  const handlePrint = () => window.print();

  // Find low stock items
  const lowStock = ingredients.filter(ing => {
    const stock = num(weightToGrams(ing.stock ?? ing.quantity ?? 0, ing.unit || 'g', ing.densityGPerMl));
    const minStock = num(ing.minStockGrams, 100);
    return stock < minStock;
  });

  return (
    <PrintOverlay>
      <style>{`
      `}</style>
      
      <div className="print-sheet">
        <div className="print-header">
          <h1 className="print-title">🛒 Shopping / Reorder List</h1>
          <p className="print-subtitle">Generated: {new Date().toLocaleDateString()}</p>
        </div>

        {lowStock.length > 0 ? (
          <div className="print-section">
            <div className="print-section-title">⚠️ Low Stock Items ({lowStock.length})</div>
            <table className="print-table">
              <thead>
                <tr>
                  <th>☐</th>
                  <th>Ingredient</th>
                  <th>Category</th>
                  <th>Current Stock</th>
                  <th>Min. Stock</th>
                  <th>Reorder Qty</th>
                  <th>Est. Cost</th>
                </tr>
              </thead>
              <tbody>
                {lowStock.map((ing, i) => {
                  const stock = num(weightToGrams(ing.stock ?? ing.quantity ?? 0, ing.unit || 'g', ing.densityGPerMl));
                  const minStock = num(ing.minStockGrams, 100);
                  const reorderQty = Math.max(minStock * 2 - stock, minStock);
                  const price = getPricePerGram(getCostPerUnit(ing), ing.unit, ing.densityGPerMl);
                  return (
                    <tr key={i}>
                      <td style={{ width: 30 }}>☐</td>
                      <td>{ing.name}</td>
                      <td>{ing.category || ing.type || '—'}</td>
                      <td style={{ color: '#d32f2f' }}>{stock}g</td>
                      <td>{minStock}g</td>
                      <td>{reorderQty}g</td>
                      <td>{formatMoney(reorderQty * price, 2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 20, background: '#e8f5e9', borderRadius: 8, marginBottom: 20 }}>
            ✅ All ingredients are well-stocked!
          </div>
        )}

        <div className="print-section">
          <div className="print-section-title">📝 Additional Items</div>
          <table className="print-table">
            <thead>
              <tr>
                <th>☐</th>
                <th>Item</th>
                <th>Quantity</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {Array(8).fill(0).map((_, i) => (
                <tr key={i}>
                  <td style={{ width: 30 }}>☐</td>
                  <td style={{ minWidth: 200 }}></td>
                  <td style={{ minWidth: 80 }}></td>
                  <td></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Shopping List
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 6. ALKALI HANDLING REFERENCE
// ==========================================
export const SafetyDataSheet = ({ onClose }) => {
  const handlePrint = () => window.print();

  return (
    <PrintOverlay>
      <style>{`
        .print-overlay .safety-box { padding: 12px; margin: 10px 0; border-radius: 6px; }
        .print-overlay .safety-box.danger { background: #ffebee; border-left: 4px solid #d32f2f; }
        .print-overlay .safety-box.warning { background: #fff3e0; border-left: 4px solid #ff9800; }
        .print-overlay .safety-box.info { background: #e3f2fd; border-left: 4px solid #2196f3; }
        .print-overlay .safety-box.success { background: #e8f5e9; border-left: 4px solid #4caf50; }
      `}</style>
      
      <div className="print-sheet">
        <div className="print-header" style={{ borderColor: '#d32f2f' }}>
          <h1 className="print-title" style={{ color: '#b91c1c' }}>⚠️ Alkali Handling Reference</h1>
          <p className="print-subtitle">NaOH and KOH — use the current supplier SDS for the actual product and concentration. This summary is not a supplier SDS.</p>
        </div>

        <div className="safety-box danger">
          <h3 style={{ margin: '0 0 10px', color: '#d32f2f' }}>🚨 HAZARD IDENTIFICATION</h3>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li><strong>Corrosive:</strong> Causes severe skin burns and eye damage</li>
            <li><strong>Reactive:</strong> Generates heat when mixed with water</li>
            <li><strong>Exposure:</strong> Avoid corrosive dust, mist, splashes and skin/eye contact</li>
          </ul>
        </div>

        <div className="print-section">
          <h3>👔 REQUIRED PERSONAL PROTECTIVE EQUIPMENT</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="safety-box info">☐ Safety goggles (chemical splash proof)</div>
            <div className="safety-box info">☐ Gloves selected for the chemical/concentration and exposure time in the SDS</div>
            <div className="safety-box info">☐ Long-sleeved shirt</div>
            <div className="safety-box info">☐ Long pants and closed-toe shoes</div>
            <div className="safety-box info">☐ Protective clothing specified by the SDS</div>
            <div className="safety-box info">☐ Face protection specified by the SDS; eyewash available</div>
          </div>
        </div>

        <div className="print-section">
          <h3>✅ SAFE HANDLING PROCEDURES</h3>
          <div className="safety-box success">
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li><strong>ALWAYS add lye TO water</strong> — never water to lye</li>
              <li>Work in a well-ventilated area or outdoors</li>
              <li>Use containers confirmed compatible with the alkali, concentration and temperature by their supplier</li>
              <li>Never use aluminum containers (causes reaction)</li>
              <li>Use the ventilation/control measures in the SDS; avoid inhaling dust or mist</li>
              <li>Keep children and pets away from workspace</li>
              <li>Label all containers clearly</li>
              <li>Store lye in airtight container away from moisture</li>
            </ol>
          </div>
        </div>

        <div className="print-section">
          <h3>🆘 FIRST AID MEASURES</h3>
          <div className="safety-box warning">
            <p style={{ margin: '0 0 10px' }}><strong>SKIN CONTACT:</strong></p>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Remove contaminated clothing. For dry NaOH, carefully brush off solids while protecting the eyes before irrigation.</li>
              <li>Immediately flush exposed skin with running water and follow the supplier SDS.</li>
              <li>Do not apply a neutralizing chemical to skin.</li>
              <li>Obtain urgent medical advice; burns can be serious even when early symptoms are mild.</li>
            </ol>
          </div>
          <div className="safety-box warning">
            <p style={{ margin: '0 0 10px' }}><strong>EYE CONTACT:</strong></p>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Immediately irrigate eyes with plenty of water; follow the SDS and emergency medical instructions</li>
              <li>Hold eyelids open while rinsing</li>
              <li>Seek immediate medical attention</li>
            </ol>
          </div>
          <div className="safety-box warning">
            <p style={{ margin: '0 0 10px' }}><strong>INHALATION:</strong></p>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              <li>Move to fresh air immediately</li>
              <li>If breathing is difficult, seek medical attention</li>
            </ol>
          </div>
        </div>

        <div className="print-section">
          <h3>🧹 SPILL CLEANUP</h3>
          <div className="safety-box info">
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>Keep people away and isolate the spill area.</li>
              <li>Follow SDS Section 6 and the site spill procedure; only trained, equipped personnel should clean up.</li>
              <li>Use compatible collection/absorbent materials and disposal methods specified for the product.</li>
              <li>Do not improvise neutralization or add water to a bulk alkali spill.</li>
            </ul>
          </div>
        </div>

        <div className="print-section">
          <h3>Emergency plan and references</h3>
          <p>Verified local emergency contact: ____________________<br/>Supplier emergency contact / SDS revision: ____________________</p>
          <p>For exposure, contact local emergency medical services and provide the product SDS.</p>
          <p><a href="https://www.cdc.gov/niosh/npg/npgd0565.html">NIOSH: sodium hydroxide</a> · <a href="https://www.cdc.gov/niosh/npg/npgd0523.html">NIOSH: potassium hydroxide</a> · <a href="https://wwwn.cdc.gov/TSP/MMG/MMGDetails.aspx?mmgid=246&amp;toxid=45">ATSDR: sodium hydroxide exposure</a></p>
        </div>

        <div className="print-footer">
          <p>Keep current supplier SDS documents and the site emergency plan accessible. Review product-specific instructions before each session.</p>
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print Safety Sheet
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// 7. QUALITY CONTROL LOG
// ==========================================
export const QualityControlSheet = ({ batches = [], onClose }) => {
  const handlePrint = () => window.print();

  return (
    <PrintOverlay>
      <style>{`
        .qc-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
        .qc-table th, .qc-table td { border: 1px solid #ccc; padding: 8px; text-align: center; }
        .qc-table th { background: #f5f5f5; }
        .rating-cell { display: flex; gap: 4px; justify-content: center; }
        .rating-box { width: 16px; height: 16px; border: 1px solid #999; }
      `}</style>
      
      <div className="print-sheet">
        <div className="print-header">
          <h1 className="print-title">✅ Quality Control Log</h1>
          <p className="print-subtitle">Post-cure testing checklist for finished soap batches</p>
        </div>

        <div className="print-section">
          <p><strong>Testing Instructions:</strong> Use a documented, validated procedure and formulation-specific acceptance specifications. Record the sample, method/revision, instruments, results and reviewer. Ratings below record observations; batch release requires the full QC review.</p>
        </div>

        <table className="qc-table">
          <thead>
            <tr>
              <th rowSpan={2}>Batch Name</th>
              <th rowSpan={2}>Test Date</th>
              <th rowSpan={2}>pH</th>
              <th colSpan={5}>Quality Ratings (1-5)</th>
              <th rowSpan={2}>Pass?</th>
              <th rowSpan={2}>Notes</th>
            </tr>
            <tr>
              <th>Hardness</th>
              <th>Lather</th>
              <th>Scent</th>
              <th>Color</th>
              <th>Texture</th>
            </tr>
          </thead>
          <tbody>
            {batches.length > 0 ? batches.map((batch, i) => (
              <tr key={i}>
                <td>{batch.name || `Batch ${i + 1}`}</td>
                <td></td>
                <td></td>
                <td></td><td></td><td></td><td></td><td></td>
                <td>☐ Y ☐ N</td>
                <td style={{ minWidth: 100 }}></td>
              </tr>
            )) : (
              Array(12).fill(0).map((_, i) => (
                <tr key={i}>
                  <td style={{ minWidth: 120 }}></td>
                  <td></td>
                  <td></td>
                  <td></td><td></td><td></td><td></td><td></td>
                  <td>☐ Y ☐ N</td>
                  <td style={{ minWidth: 100 }}></td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        <div className="print-section" style={{ marginTop: 20 }}>
          <h4>QC procedure and release record</h4>
          <p><strong>Method / revision:</strong> ____________________ <strong>Sample / dilution / temperature:</strong> ____________________</p>
          <p><strong>Instrument / calibration:</strong> ____________________ <strong>Acceptance specification:</strong> ____________________</p>
          <p>Measure pH using the validated sample preparation and calibrated equipment in your procedure. A generic pH range does not measure free caustic alkali or establish product safety.</p>
          <p>Where applicable, use an appropriate analytical method for free caustic alkali and other required quality attributes. <a href="https://www.iso.org/standard/4478.html">ISO 456</a> describes analytical methods for free caustic alkali in soaps; applicability must be reviewed for the actual formulation.</p>
          <p>Do not taste soap or use oral contact as a test. Hold nonconforming batches for investigation; additional cure time is not an automatic corrective action.</p>
          <p>Record appearance, odour, weight and other physical observations against the approved specification. Use samples on skin only under an approved testing procedure after required chemical checks.</p>
          <p><strong>Release reviewer / date:</strong> ____________________ <strong>Disposition:</strong> ☐ Hold ☐ Released ☐ Rejected</p>
          <p>Reference: <a href="https://www.fda.gov/cosmetics/cosmetics-guidance-documents/good-manufacturing-practice-gmp-guidelinesinspection-checklist-cosmetics">FDA cosmetic GMP laboratory controls and records</a>.</p>
        </div>

        <div className="no-print" style={{ marginTop: 20, display: 'flex', gap: 10 }}>
          <button onClick={handlePrint} style={{ padding: '12px 24px', background: 'var(--sl-primary)', color: '#fff', border: 'none', borderRadius: 0, fontWeight: 600, cursor: 'pointer' }}>
            🖨️ Print QC Log
          </button>
          <button onClick={onClose} style={{ padding: '12px 24px', background: '#fff', color: 'var(--sl-gray-900)', border: '1px solid var(--sl-gray-300)', borderRadius: 0, fontWeight: 500, cursor: 'pointer' }}>
            ✕ Close
          </button>
        </div>
      </div>
    </PrintOverlay>
  );
};

// ==========================================
// MAIN PRINT CENTER COMPONENT
// ==========================================
function PrintCenter() {
  const [recipes, setRecipes] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [activeSheet, setActiveSheet] = useState(null);
  const { currencyCode } = useAuth();
  const currency = currencyCode || 'INR';
  const formatMoney = (value, fractionDigits = 2) =>
    formatCurrency(num(value, 0), currency, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

  // Load data
  useEffect(() => {
    const unsubs = [];
    try {
      unsubs.push(subscribeToUserCollection('recipes', (data) => {
        // Sort by createdAt descending
        const sorted = [...data].sort((a, b) => {
          if (!a.createdAt) return 1;
          if (!b.createdAt) return -1;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setRecipes(sorted.map(normalizeRecipe));
      }));
      unsubs.push(subscribeToUserCollection('ingredients', (data) => {
        setIngredients(data);
      }));
      unsubs.push(subscribeToUserCollection('batches', (data) => {
        setBatches(data);
      }));
    } catch (error) {
      console.error('Error loading data:', error);
    }
    return () => unsubs.forEach(u => u());
  }, []);

  useEffect(() => {
    const stored = sessionStorage.getItem('printRecipe');
    if (!stored) return;
    try { setSelectedRecipe(normalizeRecipe(JSON.parse(stored))); }
    catch (error) { console.error('Unable to open print recipe:', error); }
    finally { sessionStorage.removeItem('printRecipe'); }
  }, []);

  const printOptions = [
    { id: 'recipe', name: 'Recipe Print Sheet', desc: 'Full recipe details on A4', needsRecipe: true, color: '#1976d2' },
    { id: 'production', name: 'Production Batch Sheet', desc: 'Step-by-step checklist', needsRecipe: true, color: '#4CAF50' },
    { id: 'curing', name: 'Curing Tracker', desc: 'Track batches during cure', needsRecipe: false, color: '#ff9800' },
    { id: 'labels', name: 'Soap Labels', desc: 'Print labels for bars', needsRecipe: true, color: '#9c27b0' },
    { id: 'shopping', name: 'Shopping List', desc: 'Reorder low-stock items', needsRecipe: false, color: '#00796b' },
    { id: 'safety', name: 'Alkali Handling Reference', desc: 'Supplier SDS and emergency-plan checklist', needsRecipe: false, color: '#d32f2f' },
    { id: 'qc', name: 'Quality Control Log', desc: 'Post-cure testing checklist', needsRecipe: false, color: '#795548' },
  ];

  const openSheet = (sheetId) => {
    const option = printOptions.find(o => o.id === sheetId);
    if (option?.needsRecipe && !selectedRecipe) {
      alert('Please select a recipe first');
      return;
    }
    setActiveSheet(sheetId);
  };

  return (
    <>
      <div className="sl-page print-center-page">
        <div className="pageheader borderleft">
          <Breadcrumbs items={[
            { label: 'Home', to: '/' },
            { label: 'Print Center' }
          ]} />
          <div className="pageheading">Print Center</div>
          <div className="pagedescription">Print recipes, production sheets, labels, handling references and QC records.</div>
        </div>

        <div className="print-center-content borderleft">
          <section className="mypadding borderbottom">
            {/* Recipe Selector */}
            <div className="prt-recipe-selector">
              <label className="prt-selector-label">Select Recipe (for recipe-specific prints):</label>
              <select
                className="prt-selector-select"
                value={selectedRecipe?.id || ''}
                onChange={(e) => setSelectedRecipe(recipes.find(r => r.id === e.target.value) || null)}
              >
                <option value="">— Select a recipe —</option>
                {recipes.map(r => (
                  <option key={r.id} value={r.id}>{r.name || 'Untitled'} — {r.bars || '?'} bars</option>
                ))}
              </select>
              {selectedRecipe && (
                <div className="prt-selected-info">
                  <strong>{selectedRecipe.name}</strong> — {(selectedRecipe.oils || selectedRecipe.items || []).length} oils, {selectedRecipe.bars} bars, {formatMoney(num(selectedRecipe.totals?.perBar, 0), 2)}/bar
                </div>
              )}
            </div>

            {/* Print Options Grid */}
            <div className="prt-options-grid">
              {printOptions.map(option => (
                <div
                  key={option.id}
                  className="prt-option-card"
                  onClick={() => openSheet(option.id)}
                >
                  <div className="prt-option-name">{option.name}</div>
                  <div className="prt-option-desc">{option.desc}</div>
                  {option.needsRecipe && (
                    <div className={`prt-option-status ${selectedRecipe ? 'ready' : 'needs-recipe'}`}>
                      {selectedRecipe ? 'Recipe selected' : 'Select a recipe first'}
                    </div>
                  )}
                  <button className="prt-option-btn">
                    Open & Print
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

      {/* Print Sheet Modals */}
      {activeSheet === 'recipe' && selectedRecipe && (
        <RecipePrintSheet recipe={selectedRecipe} onClose={() => setActiveSheet(null)} formatMoney={formatMoney} />
      )}
      {activeSheet === 'production' && selectedRecipe && (
        <ProductionBatchSheet recipe={selectedRecipe} onClose={() => setActiveSheet(null)} />
      )}
      {activeSheet === 'curing' && (
        <CuringTrackerSheet batches={batches} onClose={() => setActiveSheet(null)} />
      )}
      {activeSheet === 'labels' && selectedRecipe && (
        <SoapLabelsSheet recipe={selectedRecipe} onClose={() => setActiveSheet(null)} />
      )}
      {activeSheet === 'shopping' && (
        <ShoppingListSheet ingredients={ingredients} recipes={recipes} onClose={() => setActiveSheet(null)} formatMoney={formatMoney} />
      )}
      {activeSheet === 'safety' && (
        <SafetyDataSheet onClose={() => setActiveSheet(null)} />
      )}
      {activeSheet === 'qc' && (
        <QualityControlSheet batches={batches} onClose={() => setActiveSheet(null)} />
      )}
      <Footer />
      </div>
    </>
  );
}

export default PrintCenter;
